/**
 * 「メンバー」の画面（/p/:projectId/:env/members・2026-10-22 core/handoff-1b・共同作業者）。
 * 判断（入力の状態・エラーの文・役割）は projectMembers.ts（テストあり）。API の正は apps/server/src/modules/platform/projectMembers.ts。
 *
 * 持ち主: できること / できないことの説明 → 一覧（外す）→ 加えるフォーム（再認証 = components/Reauth.tsx の run・受け渡しの招きと同じ）
 * 共同作業者: 同じ説明 → 一覧（他人のメールは伏せ字でサーバーが返す）→「手伝いをやめる」
 * WHY 説明を一覧より先に置くか: 加える前に「相手は公開中のアプリも新しくできる・利用料は持ち主」を読んでほしい（founder 10/21 の決定 1・2）
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ApiError } from '../api';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { useReauth } from '../components/Reauth';
import { ConfirmDialog, ErrorState, PageHeader, Spinner } from '../components/ui';
import { isEmailUnverified } from '../emailVerify';
import { useResource } from '../hooks';
import { errorText, formatDate, useT } from '../i18n';
import { leaveApiPath, MEMBER_ERRORS, memberApiPath, memberEmailState, membersApiPath, memberStatusKey, type MemberView, type MembersView } from '../projectMembers';
import { projectEyebrow } from '../projectNav';
import { useSession } from '../session';
import { useShell } from './ProjectShell';

const INVITE_ACTION = 'members:invite';

export function MembersPage() {
  const { project } = useShell();
  const { developer } = useSession();
  const { t } = useT();
  const navigate = useNavigate();
  const reauth = useReauth();
  const view = useResource<MembersView>(membersApiPath(project.id));
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MemberView | null>(null);
  const [leaving, setLeaving] = useState(false);

  const v = view.data;
  const owner = v?.role === 'owner';
  const state = memberEmailState(email, { ownEmail: developer?.email, existing: v?.members.map((m) => m.email) ?? [], limit: v?.limit ?? 20 });

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setUnverified(false);
    setDone(null);
    try {
      await fn();
    } catch (e) {
      if (isEmailUnverified(e as ApiError)) setUnverified(true);
      else setError(errorText(e as ApiError, MEMBER_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const invite = () =>
    run(async () => {
      if (state !== 'ok') return;
      const r = await reauth.run((proof) => api<{ member: MemberView }>(membersApiPath(project.id), { method: 'POST', body: { email: email.trim(), ...proof } }), { action: INVITE_ACTION });
      if (r === null) return; // 確認を取り消した = 何も送っていない
      setDone(t('members.invite.sent', { email: r.member.email }));
      setEmail('');
      view.reload();
    });

  const remove = (m: MemberView) =>
    run(async () => {
      await api(memberApiPath(project.id, m.id), { method: 'DELETE' });
      setRemoving(null);
      setDone(t('members.remove.done', { email: m.email }));
      view.reload();
    });

  const leave = () =>
    run(async () => {
      await api(leaveApiPath(project.id), { method: 'POST' });
      setLeaving(false);
      // 抜けたらこのプロジェクトは開けない = プロジェクトの一覧へ
      navigate('/', { replace: true });
    });

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('members.title')} lead={t(owner || !v ? 'members.lead.owner' : 'members.lead.member')} />
      {reauth.dialog}
      {view.loading && !v && <Spinner />}
      {view.error && <ErrorState error={view.error} onRetry={view.reload} />}
      {done && (
        <p className="alert alert-ok" role="status">
          {done}
        </p>
      )}
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
      {v && (
        <>
          <section className="card">
            <h2 className="card-title">{t('members.can.title')}</h2>
            <p>{t('members.can.body')}</p>
            <h2 className="card-title">{t('members.cannot.title')}</h2>
            <p>{t('members.cannot.body')}</p>
          </section>

          <section className="card">
            <ul className="members-list">
              <li>
                <strong>{t('members.owner')}</strong> · {v.owner.email ?? '—'} {v.owner.you && <span className="muted">{t('members.you')}</span>}
              </li>
              {v.members.map((m) => (
                <li key={m.id} className="members-row">
                  <span>
                    {m.email} {m.you && <span className="muted">{t('members.you')}</span>}
                    <span className="small muted"> · {t(memberStatusKey(m))} · {formatDate(m.joinedAt ?? m.invitedAt)}</span>
                  </span>
                  {owner && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoving(m)} disabled={busy}>
                      {t('members.remove')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {v.members.length === 0 && <p className="muted">{t('members.empty')}</p>}
            {!owner && (
              <button type="button" className="btn btn-ghost" onClick={() => setLeaving(true)} disabled={busy}>
                {t('members.leave')}
              </button>
            )}
          </section>

          {owner && (
            <section className="card">
              <h2 className="card-title">{t('members.invite.title')}</h2>
              <form
                className="form-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  void invite();
                }}
              >
                <label className="field">
                  <span>{t('members.invite.label')}</span>
                  <input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
                </label>
                <p className="small muted">{t('members.invite.help')}</p>
                {email.trim() && state === 'invalid' && <p className="small muted">{t('members.invite.invalid')}</p>}
                {state === 'self' && <p className="field-error">{t('members.invite.self')}</p>}
                {state === 'exists' && <p className="field-error">{t('members.invite.exists')}</p>}
                {state === 'full' && <p className="field-error">{t('members.invite.full', { limit: v.limit })}</p>}
                {unverified && <EmailUnverifiedNotice />}
                <button type="submit" className="btn btn-primary" disabled={busy || state !== 'ok'}>
                  {busy ? t('members.invite.sending') : t('members.invite.send')}
                </button>
              </form>
            </section>
          )}
        </>
      )}
      <ConfirmDialog
        open={!!removing}
        title={t('members.remove.confirmTitle', { email: removing?.email ?? '' })}
        confirmLabel={t('members.remove')}
        tone="danger"
        busy={busy}
        onConfirm={() => removing && void remove(removing)}
        onClose={() => setRemoving(null)}
      >
        <p>{t('members.remove.confirmBody')}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={leaving}
        title={t('members.leave.confirmTitle')}
        confirmLabel={t('members.leave')}
        tone="danger"
        busy={busy}
        onConfirm={() => void leave()}
        onClose={() => setLeaving(false)}
      >
        <p>{t('members.leave.confirmBody')}</p>
      </ConfirmDialog>
    </>
  );
}
